import { useState, useEffect, useRef } from 'react';
import type Department from '../models/department/department';
import { getAllDepartmentsByOfficeId } from '../services/department.services';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface InitialDepartmentState {
  readonly initialized: boolean;
  readonly error?: boolean;
}

interface DepartmentsState extends InitialDepartmentState {
  readonly departments?: Department[];
}

interface Props {
  readonly id: string | undefined;
}

const useDepartments = ({ id }: Props): DepartmentsState => {
  const [state, setState] = useState<DepartmentsState>({
    initialized: false,
  });
  const keycloak = useKeycloak();

  // Held in a ref and kept out of the dependency array on purpose. The context
  // value is memoised on `tokenParsed`, which changes every time the token is
  // refreshed — a few minutes apart. Depending on it re-ran this effect on each
  // refresh, blanking the card and refetching mid-session.
  const keycloakRef = useRef(keycloak);
  keycloakRef.current = keycloak;

  // Keyed on the office id, and reset when it changes.
  //
  // This used to guard the fetch with `if (!state.initialized)` while `id` sat
  // in the dependency array: the effect re-ran when the office changed, then
  // declined to fetch because the hook had already initialised for the previous
  // one. The route is `/offices/:id/edit`, and React Router reuses the element
  // when only the param changes, so the screen kept showing the office you came
  // from — its departments, under this office's name.
  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      if (!id || id === '') {
        return;
      }

      try {
        const result = (await getAllDepartmentsByOfficeId(id, keycloakRef.current)) ?? [];
        if (!cancelled) {
          setState({ initialized: true, departments: result });
        }
      } catch (error) {
        if (!cancelled) {
          setState({ initialized: false, error: true });
        }
      }
    };

    // Drop the previous office's data before the new request lands, so nothing
    // renders one office's departments under another's heading in between.
    setState({ initialized: false });
    load();

    return () => {
      cancelled = true;
    };
  }, [id]);

  return state;
};

export default useDepartments;
